import classNames from 'classnames';
import { React, useEffect } from 'react';
import { render } from 'react-dom';
import ArticleFooter from '../../ArticleFooter';
import ArticleHeader from '../../ArticleHeader';
import * as Render from '../../Base/Render';
import Breadcrumb from '../../Breadcrumb';
import CommentForm from '../../CommentForm';
import withSensitiveImage from '../../Containers/withSensitiveImage';
import withSrcSets from '../../Containers/withSrcSets';
import DiagnosticPanel from '../../DiagnosticPanel';
import ImageBlock from '../../ImageBlock';
import InfoBanner from '../../InfoBanner';
import InfoBannerVardpersonal from '../../InfoBannerVardpersonal';
import InformationType from '../../InformationType';
import Localnav from '../../Localnav';
import MatomoTrackMediaAnalytics from '../../Matomo/matomo-track-media-analytics';
import ReferenceToolTip from '../../Reference/reference-tooltip';
import ReferencePanel from '../../ReferencePanel';
import Share from '../../Share';
import Theme from '../../Theme';

const ImageBlockWithSrcSets = withSrcSets(withSensitiveImage(ImageBlock));

const ArticlePage = (props) => {
  const {
    ArticleTools,
    FeedbackForm,
    PageUrl,
    RatingComponent,
    MainContentGrid,
    SidebarGrid,
    contentDirection,
    renderReferences,
  } = props;

  const isArticlePage = true;

  const InfoBannerSubject =
    props?.Content?.Translations &&
    props?.Content?.InformationType &&
    props.Content.Translations[props.Content.InformationType];

  useEffect(() => {
    if (renderReferences) {
      if (typeof window === 'undefined') return;
      window.document
        .querySelectorAll('.reference')
        .forEach(
          (refNode) =>
            refNode.dataset.modalText &&
            refNode.dataset.buttonId &&
            refNode.dataset.label &&
            render(
              <ReferenceToolTip
                referenceText={refNode.dataset.modalText}
                componentId={refNode.dataset.buttonId}
                label={refNode.dataset.label}
              />,
              refNode
            )
        );
    }
  }, []);

  const articleMainClasses = classNames({
    'row u-article-main': true,
    'u-article-main--has-info-banner': props?.HasInfoBanner,
  });

  const sideBarEnabled = () => {
    const { PageTypeName, FeatureToggleSettings } = props;

    return (
      FeatureToggleSettings?.UsePrintSidebar && PageTypeName !== 'SelfTestPage'
    );
  };

  return (
    <>
      <div className="breadcrumb-container">
        <div className="row">
          <div className="columns">
            <Breadcrumb {...props.Breadcrumbs} Language={props.Language} />
          </div>
        </div>
      </div>
      <main className="main">
        <div className="article-main-content">
          <div
            id="content"
            className="article-main-content__inner"
            dir={props.Dir}
          >
            <Theme {...props}>
              <InformationType {...props} />
              {props.HasInfoBanner && {
                ...(props?.FeatureToggleSettings?.Use1177ProfessionDesign ? (
                  <InfoBannerVardpersonal
                    Subject={InfoBannerSubject}
                    Title={props.Content?.Heading}
                    IsMobile={props.IsMobile}
                    IsHidden={props.IsHidden}
                    InformationType={props?.Content?.InformationType}
                    Switch={props.HasTargetGroupSwitch && props.Switch}
                    AnchorNavigation={props.AnchorNavigation}
                    AnchorHeading={props.AnchorHeading}
                  />
                ) : (
                  <InfoBanner
                    Subject={InfoBannerSubject}
                    Title={props.Content?.Heading}
                    IsMobile={props.IsMobile}
                    IsHidden={props.IsHidden}
                    InformationType={props?.Content?.InformationType}
                    Switch={props.HasTargetGroupSwitch && props.Switch}
                    AnchorNavigation={props.AnchorNavigation}
                    AnchorHeading={props.AnchorHeading}
                  />
                )),
              }}
              <div className={articleMainClasses}>
                <div className={MainContentGrid}>
                  <ArticleHeader
                    Modifiers={{ 'c-articleheader--article-page': true }}
                    Heading={Render.Property(props.Content.Heading, 'Heading')}
                    HeadingTranslation={props.Content.HeadingTranslation}
                    Preamble={Render.Property(
                      props.Content.Preamble,
                      'Preamble'
                    )}
                    SubjectAboveHeading={props.SubjectAboveHeading}
                    {...props}
                  >
                    {props.Content.ShowArticleTools && ArticleTools && (
                      <ArticleTools {...props} />
                    )}
                  </ArticleHeader>

                  {props.Content?.CodeSet?.codeGroups?.length > 0 && (
                    <DiagnosticPanel
                      collapsed={true}
                      Translations={props.Content.Translations}
                      {...props.Content.CodeSet}
                    />
                  )}
                  {props.Content?.TopMediaImage || props.Content?.Image ? (
                    <Render.Block
                      blocks={
                        props.Content.TopMediaImage || props.Content.Image
                      }
                      propertyName="Image"
                      currentPageProps={props}
                    >
                      <ImageBlockWithSrcSets
                        UseZoom={false}
                        Modifiers={{ 'c-imageblock--main-image': true }}
                        Sizes={{
                          sizes: '(min-width: 1024px) 833px, 100vw',
                        }}
                      />
                    </Render.Block>
                  ) : null}
                  {!props.RenderMainContent ? (
                    <div className="u-blocklist">
                      <Render.Block
                        blocks={props.Content.MainContent}
                        propertyName="MainContent"
                        currentPageProps={props}
                        isArticlePage={isArticlePage}
                      >
                        {props.BlockTypeRenderer}
                      </Render.Block>
                    </div>
                  ) : (
                    props.RenderMainContent
                  )}
                  {RatingComponent}
                  <Share
                    PrintsideBarEnabled={sideBarEnabled()}
                    {...props.Content}
                  />
                  {props.Content.referenceList && (
                    <ReferencePanel
                      collapsed={true}
                      referenceList={props.Content.referenceList}
                    />
                  )}

                  {props.Content.ArticleFooter && props.FooterHeading && (
                    <h2 className="c-articlefooter__heading">
                      {props.FooterHeading}
                    </h2>
                  )}

                  {props.Content.ArticleFooter && (
                    <>
                      {props.NationalHeading && (
                        <div
                          className="c-articlefooter__subheading"
                          data-cy="c-articlefooter-subheading"
                        >
                          {props.NationalHeading}
                        </div>
                      )}
                      <ArticleFooter
                        contentDirection={contentDirection}
                        FooterHasCollapsibleRoleGroups={
                          props.FooterHasCollapsibleRoleGroups
                        }
                        ArticleFooter={props.Content.ArticleFooter}
                        Translations={props.Content.Translations}
                        Site={props.Site}
                      />
                    </>
                  )}
                  {props.Content.RegionalArticleFooter && (
                    <>
                      {props.RegionalHeading && (
                        <div
                          className="c-articlefooter__subheading"
                          data-cy="c-articlefooter-subheading"
                        >
                          {props.RegionalHeading}
                        </div>
                      )}

                      <ArticleFooter
                        FooterHasCollapsibleRoleGroups={
                          props.FooterHasCollapsibleRoleGroups
                        }
                        isRegional={true}
                        ArticleFooter={props.Content.RegionalArticleFooter}
                        Translations={props.Content.Translations}
                        RegionalName={props.Regionalization.CurrentRegionName}
                      />
                    </>
                  )}
                  {props.Content.HideFeedbackForm !== true && (
                    <CommentForm
                      FeedbackForm={FeedbackForm}
                      PageUrl={PageUrl}
                    />
                  )}
                </div>

                <div className={SidebarGrid}>
                  <div className="u-100-height">
                    {props.Content && props.Content.ShowLocalNav && (
                      <Localnav {...props.LocalMenu} />
                    )}
                    {props.RightColumnComponents && props.RightColumnComponents}
                  </div>
                </div>
              </div>
            </Theme>
          </div>
        </div>

        <Render.Block
          blocks={props.Content.SectionContent}
          propertyName="SectionContent"
          currentPageProps={props}
        >
          {props.BlockTypeRenderer}
        </Render.Block>

        <MatomoTrackMediaAnalytics />
      </main>
    </>
  );
};

ArticlePage.defaultProps = {
  MainContentGrid: 'columns medium-offset-2 medium-8 large-6',
  SidebarGrid: 'columns medium-2 large-3 show-for-large u-right-col',
};

export default ArticlePage;
